// The shell a client login sees: its own dashboard and opportunity pages, nothing else. The API confines
// the session to the same scope, so this is presentation, not the security boundary.
import { useEffect } from 'react';
import type { Me } from '../lib/api';
import { navigate, useHashRoute } from '../lib/router';
import { initials } from '../lib/format';
import { Icon } from './Icon';
import { PageHead } from './ui';
import { AccountPanel } from './AccountPanel';
import { ClientPage } from '../pages/ClientPage';
import { OpportunityPage } from '../pages/OpportunityPage';

const ROUTES = ['dashboard', 'opportunity', 'account'];

export function ClientShell({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const { route, params } = useHashRoute();
  const client = me.client;
  const known = ROUTES.includes(route);
  useEffect(() => { if (!known) navigate('dashboard'); }, [known]);

  if (!client) return <div className="login-wrap"><div className="muted">This account is not linked to a client profile. Ask NTN to check your access.</div></div>;

  let page;
  if (route === 'opportunity' && params[0]) page = <OpportunityPage clientId={client.id} matchId={params[0]} me={me} clientView />;
  else if (route === 'account') page = <><PageHead title="Your account" sub={client.name} /><AccountPanel me={me} /></>;
  else page = <ClientPage id={client.id} me={me} clientView />;

  return (
    <>
      <div className="classbar">PRAXIS · {client.name.toUpperCase()} · OPPORTUNITY MONITORING BY NTN</div>
      <div className="app">
        <aside className="sidebar">
          <div className="brand">
            <div className="brand-mark">PX</div>
            <div className="brand-text">
              <div className="brand-title">PRAXIS</div>
              <div className="brand-sub">Market &amp; Opportunity Intelligence</div>
            </div>
          </div>
          <nav className="nav">
            <div className="nav-group">
              <div className="nav-group-label">{client.name}</div>
              <a className={`nav-item ${route !== 'account' ? 'active' : ''}`} href="#/dashboard"><Icon name="clients" /><span>Your dashboard</span></a>
              <a className={`nav-item ${route === 'account' ? 'active' : ''}`} href="#/account"><Icon name="admin" /><span>Account</span></a>
            </div>
          </nav>
          <div className="nav-footer">
            <div className="client-pill"><span className="dot"></span> Monitored by {me.tenant.name}</div>
            <div className="powered">Gravel Road · Praxis</div>
          </div>
        </aside>
        <main className="main">
          <header className="topbar">
            <div className="muted sm" style={{ flex: 1 }}>Opportunities NTN has identified and assessed for {client.name}. Questions or feedback go to NTN through the notes on each page.</div>
            <div className="topbar-right">
              <a className="avatar" href="#/account" title={`${me.user.email} · account`}>{initials(me.user.display_name || me.user.email)}</a>
              <button className="ghost-btn" onClick={onLogout}>Sign out</button>
            </div>
          </header>
          <div className="view">{page}</div>
        </main>
      </div>
    </>
  );
}
